import { For, lazy, type Component, type JSX } from 'solid-js';
import { Navigate, Route, Router, useParams } from '@solidjs/router';
import { AppShell } from '../components/AppShell';
import { beginNavigationCodeLoad } from '../services/navigation-telemetry';
import { RouteAuthorization } from './RouteAuthorization';
import type { RouteCapability } from './route-access';
import { workspaceRouteDescriptorById } from './workspace-route-descriptors';
import type { PrimaryWorkspaceRouteId } from './workspace-route-metadata';

const navigationLazy = (loader: () => Promise<{ default: Component }>) =>
  lazy(async () => {
    const finishCodeLoad = beginNavigationCodeLoad();
    try {
      return await loader();
    } finally {
      finishCodeLoad();
    }
  });

const primaryNavigationLazy = (id: PrimaryWorkspaceRouteId) =>
  navigationLazy(() => workspaceRouteDescriptorById(id).loadComponent());

const DashboardPage = primaryNavigationLazy('overview');
const PeoplePage = primaryNavigationLazy('people');
const AccountPage = navigationLazy(() =>
  import('./AccountPage').then((module) => ({ default: module.AccountPage })),
);
const AccountSecurityPage = navigationLazy(() =>
  import('./AccountSecurityPage').then((module) => ({
    default: module.AccountSecurityPage,
  })),
);
const AdminAccessPage = primaryNavigationLazy('access');
const EnrollmentProgressPage = navigationLazy(() =>
  import('./EnrollmentProgressPage').then((module) => ({
    default: module.EnrollmentProgressPage,
  })),
);
const GroupsPage = primaryNavigationLazy('groups');
const LevelPlanLibraryPage = primaryNavigationLazy('curriculum');
const LevelPlanDetailPage = navigationLazy(() =>
  import('./LevelPlanDetailPage').then((module) => ({
    default: module.LevelPlanDetailPage,
  })),
);
const CatalogsPage = navigationLazy(() =>
  import('./CatalogsPage').then((module) => ({ default: module.CatalogsPage })),
);
const LearningResourcesPage = navigationLazy(() =>
  import('./LearningResourcesPage').then((module) => ({
    default: module.LearningResourcesPage,
  })),
);
const RubricsPage = navigationLazy(() =>
  import('./RubricsPage').then((module) => ({ default: module.RubricsPage })),
);
const SchedulePage = primaryNavigationLazy('schedule');
const PresentationLibraryPage = primaryNavigationLazy('presentations');
const PresentationEditorPage = navigationLazy(() =>
  import('./PresentationEditorPage').then((module) => ({
    default: module.PresentationEditorPage,
  })),
);
const PresentationPreviewPage = navigationLazy(() =>
  import('./PresentationPreviewPage').then((module) => ({
    default: module.PresentationPreviewPage,
  })),
);
const PresentationDetailPage = navigationLazy(() =>
  import('./PresentationDetailPage').then((module) => ({
    default: module.PresentationDetailPage,
  })),
);
const PresentationTemplatesPage = navigationLazy(() =>
  import('./PresentationTemplatesPage').then((module) => ({
    default: module.PresentationTemplatesPage,
  })),
);
const PresentationAssetsPage = navigationLazy(() =>
  import('./PresentationAssetsPage').then((module) => ({
    default: module.PresentationAssetsPage,
  })),
);
const MeetingPage = navigationLazy(() =>
  import('./MeetingPage').then((module) => ({ default: module.MeetingPage })),
);
const DeliveriesPage = primaryNavigationLazy('deliveries');
const SelfPacedReaderPage = navigationLazy(() =>
  import('./SelfPacedReaderPage').then((module) => ({
    default: module.SelfPacedReaderPage,
  })),
);
const RecordingPage = navigationLazy(() =>
  import('./RecordingPage').then((module) => ({ default: module.RecordingPage })),
);
const TasksPage = primaryNavigationLazy('learning');
const AssessmentPage = primaryNavigationLazy('assessment');
const MessagesPage = primaryNavigationLazy('messages');
const MessagingSafetyPage = navigationLazy(() =>
  import('./MessagesPage').then((module) => ({ default: module.MessagingSafetyPage })),
);
const PrivacyPage = primaryNavigationLazy('privacy');
const GuardianDirectoryPage = navigationLazy(() =>
  import('./PrivacyPage').then((module) => ({ default: module.GuardianDirectoryPage })),
);
const OperationsAuditPage = primaryNavigationLazy('operations');
const OperationsIntegrityPage = navigationLazy(() =>
  import('./OperationsPage').then((module) => ({ default: module.OperationsIntegrityPage })),
);
const OperationsOutboxPage = navigationLazy(() =>
  import('./OperationsPage').then((module) => ({ default: module.OperationsOutboxPage })),
);
const OperationsJobsPage = navigationLazy(() =>
  import('./OperationsPage').then((module) => ({ default: module.OperationsJobsPage })),
);
const AcademySettingsPage = primaryNavigationLazy('governance');
const AcademyRetentionPage = navigationLazy(() =>
  import('./GovernancePage').then((module) => ({ default: module.AcademyRetentionPage })),
);
const AcademyPoliciesPage = navigationLazy(() =>
  import('./GovernancePage').then((module) => ({ default: module.AcademyPoliciesPage })),
);
const AcademyLifecyclesPage = navigationLazy(() =>
  import('./GovernancePage').then((module) => ({ default: module.AcademyLifecyclesPage })),
);

type RouteModule = {
  path: string;
  capability: RouteCapability;
  component: Component;
};

const routeModules = {
  home: [{ path: '/', capability: 'overview', component: DashboardPage }],
  account: [
    { path: '/account', capability: 'account', component: AccountPage },
    { path: '/account/security', capability: 'account', component: AccountSecurityPage },
    {
      path: '/admin/access/accounts',
      capability: 'accessAdministration',
      component: AdminAccessPage,
    },
  ],
  people: [
    { path: '/people', capability: 'people', component: PeoplePage },
    { path: '/people/teachers/:teacherId', capability: 'people', component: PeoplePage },
    { path: '/people/students/:studentId', capability: 'people', component: PeoplePage },
    {
      path: '/people/guardians',
      capability: 'guardianProfiles',
      component: GuardianDirectoryPage,
    },
    {
      path: '/people/guardians/:guardianId',
      capability: 'guardianProfiles',
      component: GuardianDirectoryPage,
    },
    { path: '/groups', capability: 'groups', component: GroupsPage },
    { path: '/groups/:groupId', capability: 'groups', component: GroupsPage },
  ],
  curriculum: [
    {
      path: '/curriculum/plans',
      capability: 'curriculum',
      component: LevelPlanLibraryPage,
    },
    {
      path: '/curriculum/plans/:planId',
      capability: 'curriculum',
      component: LevelPlanDetailPage,
    },
    {
      path: '/curriculum/catalogs',
      capability: 'curriculum',
      component: CatalogsPage,
    },
    {
      path: '/curriculum/resources',
      capability: 'curriculum',
      component: LearningResourcesPage,
    },
    {
      path: '/curriculum/resources/:resourceId',
      capability: 'curriculum',
      component: LearningResourcesPage,
    },
    {
      path: '/curriculum/rubrics',
      capability: 'curriculum',
      component: RubricsPage,
    },
  ],
  schedule: [
    { path: '/schedule', capability: 'schedule', component: SchedulePage },
    { path: '/sessions/:sessionId', capability: 'schedule', component: SchedulePage },
    {
      path: '/assignments/:assignmentId',
      capability: 'schedule',
      component: SchedulePage,
    },
  ],
  studio: [
    {
      path: '/studio/presentations',
      capability: 'presentationStudio',
      component: PresentationLibraryPage,
    },
    {
      path: '/studio/presentations/versions/:versionId',
      capability: 'presentationStudio',
      component: PresentationPreviewPage,
    },
    {
      path: '/studio/presentations/versions/:versionId/edit',
      capability: 'presentationStudio',
      component: PresentationEditorPage,
    },
    {
      path: '/studio/presentations/:presentationId',
      capability: 'presentationStudio',
      component: PresentationDetailPage,
    },
    {
      path: '/studio/templates',
      capability: 'presentationStudio',
      component: PresentationTemplatesPage,
    },
    {
      path: '/studio/templates/:templateId',
      capability: 'presentationStudio',
      component: PresentationTemplatesPage,
    },
    {
      path: '/studio/assets',
      capability: 'presentationStudio',
      component: PresentationAssetsPage,
    },
  ],
  delivery: [
    { path: '/deliveries', capability: 'delivery', component: DeliveriesPage },
    { path: '/deliveries/:deliveryId', capability: 'delivery', component: DeliveriesPage },
    { path: '/learn/:deliveryId', capability: 'delivery', component: SelfPacedReaderPage },
    { path: '/meetings/:meetingId', capability: 'delivery', component: MeetingPage },
    { path: '/recordings/:recordingId', capability: 'delivery', component: RecordingPage },
  ],
  learning: [
    { path: '/learning', capability: 'learning', component: TasksPage },
    {
      path: '/learning/tasks/:studentTaskId',
      capability: 'learning',
      component: TasksPage,
    },
    {
      path: '/learning/progress/:enrollmentId',
      capability: 'learning',
      component: EnrollmentProgressPage,
    },
    { path: '/assessment', capability: 'assessment', component: AssessmentPage },
  ],
  messaging: [
    { path: '/messages', capability: 'messages', component: MessagesPage },
    { path: '/messages/:conversationId', capability: 'messages', component: MessagesPage },
  ],
  privacy: [
    { path: '/privacy', capability: 'privacy', component: PrivacyPage },
    {
      path: '/admin/privacy',
      capability: 'privacyAdministration',
      component: PrivacyPage,
    },
    {
      path: '/privacy/safety',
      capability: 'privacySafety',
      component: MessagingSafetyPage,
    },
  ],
  operations: [
    {
      path: '/operations/audit',
      capability: 'operations',
      component: OperationsAuditPage,
    },
    {
      path: '/operations/integrity',
      capability: 'operations',
      component: OperationsIntegrityPage,
    },
    {
      path: '/operations/outbox',
      capability: 'operations',
      component: OperationsOutboxPage,
    },
    {
      path: '/operations/jobs',
      capability: 'operations',
      component: OperationsJobsPage,
    },
  ],
  academy: [
    {
      path: '/academy/settings',
      capability: 'academyAdministration',
      component: AcademySettingsPage,
    },
    {
      path: '/academy/retention',
      capability: 'academyAdministration',
      component: AcademyRetentionPage,
    },
    {
      path: '/academy/policies',
      capability: 'academyAdministration',
      component: AcademyPoliciesPage,
    },
    {
      path: '/academy/lifecycles',
      capability: 'academyAdministration',
      component: AcademyLifecyclesPage,
    },
  ],
} as const satisfies Record<string, readonly RouteModule[]>;

const routes = Object.values(routeModules).flat();

const authorized = (route: RouteModule): Component => {
  const AuthorizedComponent = () => (
    <RouteAuthorization capability={route.capability} component={route.component} />
  );
  return AuthorizedComponent;
};

export const canonicalPresentationVersionPath = (
  versionId: string,
  edit = false,
  locationSuffix = '',
) =>
  `/studio/presentations/versions/${encodeURIComponent(versionId)}${edit ? '/edit' : ''}${locationSuffix}`;

export const PresentationVersionRedirect = (props: { edit?: boolean }) => {
  const params = useParams<{ versionId: string }>();
  return (
    <Navigate
      href={({ location }) =>
        canonicalPresentationVersionPath(
          params.versionId,
          props.edit,
          `${location.search}${location.hash}`,
        )
      }
    />
  );
};

export const canonicalMeetingPath = (meetingId: string, locationSuffix = '') =>
  `/meetings/${encodeURIComponent(meetingId)}${locationSuffix}`;

export const MeetingRedirect = () => {
  const params = useParams<{ meetingId: string }>();
  return (
    <Navigate
      href={({ location }) =>
        canonicalMeetingPath(params.meetingId, `${location.search}${location.hash}`)
      }
    />
  );
};

export const canonicalRecordingPath = (recordingId: string, locationSuffix = '') =>
  `/recordings/${encodeURIComponent(recordingId)}${locationSuffix}`;

export const RecordingRedirect = () => {
  const params = useParams<{ recordingId: string }>();
  return (
    <Navigate
      href={({ location }) =>
        canonicalRecordingPath(params.recordingId, `${location.search}${location.hash}`)
      }
    />
  );
};

const WorkspaceRoot = (props: { children?: JSX.Element }) => <AppShell>{props.children}</AppShell>;

export const WorkspaceRouter = () => (
  <Router root={WorkspaceRoot}>
    <For each={routes}>{(route) => <Route path={route.path} component={authorized(route)} />}</For>

    <Route path="/curriculum" component={() => <Navigate href="/curriculum/plans" />} />
    <Route path="/presentations" component={() => <Navigate href="/studio/presentations" />} />
    <Route path="/presentations/:versionId" component={PresentationVersionRedirect} />
    <Route
      path="/presentations/:versionId/edit"
      component={() => <PresentationVersionRedirect edit />}
    />
    <Route path="/meeting/:meetingId" component={MeetingRedirect} />
    <Route path="/meeting/:meetingId/recordings/:recordingId" component={RecordingRedirect} />
    <Route path="/meetings/:meetingId/recordings/:recordingId" component={RecordingRedirect} />
    <Route path="/recordings/:recordingId/versions/:versionId" component={RecordingRedirect} />
    <Route path="/tasks" component={() => <Navigate href="/learning" />} />
    <Route path="/operations" component={() => <Navigate href="/operations/outbox" />} />
    <Route path="/settings" component={() => <Navigate href="/academy/settings" />} />
  </Router>
);
